import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faCheck, faQuestion, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { MoreToggle } from "../../components/MoreToggle";
import { cubeGeometry } from "../../state/cubeGeometry";
import { usePublicaties, type Publicatie } from "../../state/publicaties";
import { Cube } from "../../state/types";
import {
  afstand,
  afstandKort,
  datum,
  datumKort,
  splits,
  typeLabel,
  uitkomst,
  type Uitkomst,
} from "./bekendmakingenFormat";
import { A, Info, SubHeader } from "./util";

const TOON = 5;

const KOLOMMEN = 3;

const ICONEN: Record<Uitkomst, { icon: IconDefinition; className: string }> = {
  open: { icon: faQuestion, className: "text-gray-500" },
  positief: { icon: faCheck, className: "text-green" },
  negatief: { icon: faXmark, className: "text-red-600" },
};

/** Neemt altijd dezelfde breedte in, zodat de typekolom recht blijft. */
const StatusIcoon = ({ status }: { status?: string }) => {
  const u = uitkomst(status);
  return (
    <span className="inline-block w-4" title={u ? status!.replace("_", " ") : undefined}>
      {u && (
        <FontAwesomeIcon icon={ICONEN[u].icon} className={`inline w-3 ${ICONEN[u].className}`} />
      )}
    </span>
  );
};

/** Eén regel in de tabel; een klik vouwt de hele bekendmaking eronder open. */
const Regel = ({ p }: { p: Publicatie }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr
        className={`cursor-pointer align-top hover:bg-bright-yellow/40 ${open ? "bg-bright-yellow/20" : ""}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <td className="px-2.5">{datumKort(p.gepubliceerdOp)}</td>
        <td className="px-1.5 text-right whitespace-nowrap">{afstandKort(p.afstandMeter)}</td>
        <td className="px-2.5 truncate" title={typeLabel(p)}>
          <StatusIcoon status={p.procedurestatus} />
          {typeLabel(p)}
        </td>
      </tr>
      {open && (
        <tr className="bg-bright-yellow/20">
          <td colSpan={KOLOMMEN} className="px-2.5 pb-2 break-words">
            <A href={p.url}>{p.titel}</A>
            <div className="text-gray-600">
              {[p.geometrielabel, p.blad, datum(p.gepubliceerdOp), afstand(p.afstandMeter)]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

/** Eigen tabel per lijst: beleid en vergunningen zijn los te lezen. */
const Lijst = ({ kop, rijen }: { kop: string; rijen: Publicatie[] }) => {
  const [meer, setMeer] = useState(false);
  if (!rijen.length) return null;
  return (
    <div className="py-1">
      <div className="px-2.5 py-1 font-semibold">
        {kop} ({rijen.length})
      </div>
      {/* Vaste kolommen: het rapport is maar ~400px breed, dus een lange soort
          kapt af in plaats van de tabel voorbij de rand te rekken */}
      <table className="w-full table-fixed">
        <thead className="text-left text-gray-600">
          <tr>
            <th className="w-28 px-2.5 font-normal">Datum</th>
            <th className="w-20 px-1.5 font-normal text-right">Afstand</th>
            <th className="px-2.5 font-normal">Type</th>
          </tr>
        </thead>
        <tbody>
          {(meer ? rijen : rijen.slice(0, TOON)).map((p) => (
            <Regel key={p.publicatieId} p={p} />
          ))}
        </tbody>
      </table>
      {rijen.length > TOON && (
        <div className="px-2.5 py-1">
          <MoreToggle open={meer} setOpen={setMeer} />
        </div>
      )}
    </div>
  );
};

/**
 * Officiële bekendmakingen rond dit perceel, uit het publicatieregister van de
 * omgevingsmonitor. De straal ligt om de geometrie van de cube, dus dezelfde
 * sectie werkt op /location, in een projectrapport (daar is de cube de unie
 * van alle percelen) en in de kaveldrawer van de workspace.
 *
 * Twee lijsten, want het zijn twee soorten nieuws. Beleid — een omgevingsvisie,
 * een verordening — zegt wat er mag gaan gelden; een vergunning zegt wat er
 * werkelijk gebeurt, meestal op het perceel ernaast. Door elkaar gezet verliest
 * het beleid het altijd: dat hangt aan een gemeente en de vergunning aan een
 * adres, dus op afstand staat de dakkapel bovenaan.
 */
export const Bekendmakingen = ({ cube }: { cube: Cube }) => {
  const geometry = useMemo(() => cube?.geometry && cubeGeometry(cube), [cube]);
  const [state, publicaties, load] = usePublicaties(
    useShallow((s) => [s.state, s.publicaties, s.load])
  );

  useEffect(() => {
    if (geometry) load(geometry);
  }, [geometry, load]);

  // Een storing bij de monitor of eenvoudigweg niets in de buurt: in beide
  // gevallen is een lege sectie eerlijker dan een foutmelding over een bron
  // die de lezer niet kent.
  if (state === "error" || !publicaties?.length) return null;

  const { beleid, vergunningen } = splits(publicaties);
  return (
    <>
      <SubHeader>Bekendmakingen</SubHeader>
      <Info>
        <Lijst kop="Beleid en plannen" rijen={beleid} />
        <Lijst kop="Vergunningen en meldingen" rijen={vergunningen} />
      </Info>
    </>
  );
};
